import { useCallback, useRef, useState } from 'react';

import { exportApi } from '../../api/export.js';
import { referenceDataApi } from '../../api/referenceData.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import { ErrorState, ExportCsvButton, LoadingState, MessageBanner } from '../../components/ui';
import { SortHeader, initialSortDirFor } from '../../components/ui/SortHeader.jsx';
import { useInfiniteScroll } from '../../hooks';
import { useUrlColumns, useUrlDebouncedParam, useUrlParam } from '../../url';
import { ColumnPicker } from './ColumnPicker.jsx';
import { FROZEN_COLS, HEADER_COLORS, TM_DEFAULT_COLS } from './constants.js';
import { formatValue } from './helpers.jsx';
import { SummaryRow } from './SummaryRow.jsx';

const FROZEN_WIDTH = 80;

// ``urlActive`` — this is the visible Rules sub-tab, so its filters own the
// query string. See DataExplorer for why the hidden explorers must not.
export function TaskMappingExplorer({ urlActive = true }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.EDIT_REFERENCE_DATA);

  const urlOpts = { active: urlActive };
  const [selectedCols,  setSelectedCols]  = useUrlColumns('cols', TM_DEFAULT_COLS, urlOpts);
  const [pickerOpen,    setPickerOpen]    = useState(false);
  const [search, setSearch, debouncedSearch] = useUrlDebouncedParam('q', urlOpts);
  const [filterPunches, setFilterPunches] = useUrlParam('punches', 'all', urlOpts);
  const [filterStatus,  setFilterStatus]  = useUrlParam('status', 'included', urlOpts);
  const [sortBy,  setSortBy]  = useUrlParam('sort_by', '', urlOpts);
  const [sortDir, setSortDir] = useUrlParam('sort_dir', 'asc', urlOpts);
  const [saving, setSaving] = useState(null);
  const [msg,    setMsg]    = useState(null);

  // The table scrolls inside its own box so the header row can stay frozen —
  // that box is also the infinite-scroll sentinel's root.
  const scrollRef = useRef(null);

  const handleSort = useCallback((col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(initialSortDirFor(col)); }
  }, [sortBy, sortDir, setSortBy, setSortDir]);

  // One filter builder for the table and the CSV — see DataExplorer.
  const queryParams = useCallback(() => ({
    include_filter: filterStatus,
    search: debouncedSearch,
    filter_punches: filterPunches,
    ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
  }), [filterStatus, debouncedSearch, filterPunches, sortBy, sortDir]);

  const fetchPage = useCallback(
    (offset, limit) => referenceDataApi.getTaskMapping({ ...queryParams(), limit, offset }),
    [queryParams],
  );

  const {
    items, summary, columns: rawColumns, total,
    loading, loadingMore, hasMore, error, sentinelRef, updateItem,
  } = useInfiniteScroll(fetchPage, [fetchPage], { rootRef: scrollRef });

  const allColumns = rawColumns.filter((c) => !c.frozen);
  const activeCols = allColumns.filter((c) => selectedCols.has(c.key));

  const toggleCol = (key) => {
    const next = new Set(selectedCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedCols(next);
  };
  const selectTable = (table) => {
    const tableKeys = allColumns.filter((c) => c.table === table).map((c) => c.key);
    const allSelected = tableKeys.every((k) => selectedCols.has(k));
    const next = new Set(selectedCols);
    tableKeys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
    setSelectedCols(next);
  };

  const handleExclChange = async (row, colKey, newValue) => {
    setSaving(`${row.row_id}_${colKey}`);
    try {
      await referenceDataApi.updateTaskExclusion(row.code, colKey, newValue);
      updateItem('row_id', row.row_id, { [colKey]: newValue });
    } catch (e) {
      setMsg({ type: 'error', text: `Save failed: ${e.message}` });
    }
    setSaving(null);
  };

  return (
    <>
      <SummaryRow summary={summary} />

      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setPickerOpen(!pickerOpen)}
          style={{ marginBottom: 8 }}
        >
          {pickerOpen ? 'Hide Columns' : 'Choose Columns'} ({activeCols.length} selected)
        </button>
        {pickerOpen && (
          <ColumnPicker
            columns={allColumns}
            selectedCols={selectedCols}
            onToggle={toggleCol}
            onSelectTable={selectTable}
          />
        )}
      </div>

      <div style={{
        display: 'flex', gap: 12, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..." style={{ width: 200 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="included">Included</option>
            <option value="excluded">Excluded</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Punches</label>
          <select value={filterPunches} onChange={(e) => setFilterPunches(e.target.value)}>
            <option value="all">All</option>
            <option value="with">With Punches</option>
            <option value="without">No Punches</option>
          </select>
        </div>
        <ExportCsvButton
          onExport={() => exportApi.download('task_mapping', {
            params: queryParams(),
            // Task Code + the chosen columns + the frozen exclusion switches:
            // the table, left to right.
            columns: ['row_id', ...activeCols.map((c) => c.key),
                      ...FROZEN_COLS.map((c) => c.key)],
          })}
          disabled={loading || total === 0 || activeCols.length === 0}
          title="Download every row matching these filters as CSV, with the columns shown here — not just the rows loaded"
          onError={(text) => setMsg({ type: 'error', text })}
          style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
        />
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {items.length} of {total}
        </div>
      </div>

      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />
      {error && <ErrorState>{error}</ErrorState>}

      {loading
        ? <LoadingState />
        : activeCols.length === 0
          ? <div className="alert alert-info">
              No columns selected. Click "Choose Columns" to pick which data to display.
            </div>
          : (
            <div className="table-scroll" ref={scrollRef}>
              <table>
                <thead>
                  <tr>
                    <SortHeader col="row_id" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>
                      Task Code
                    </SortHeader>
                    {activeCols.map((col) => (
                      <SortHeader
                        key={col.key} col={col.key}
                        sortBy={sortBy} sortDir={sortDir} onClick={handleSort}
                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                      >
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: HEADER_COLORS[col.group] || '#ccc' }}>
                          {col.table || col.group}
                        </span>
                        {col.label}
                      </SortHeader>
                    ))}
                    {FROZEN_COLS.map((fc, i) => (
                      <th key={fc.key} style={{
                        fontSize: 11, whiteSpace: 'nowrap',
                        width: FROZEN_WIDTH, minWidth: FROZEN_WIDTH, maxWidth: FROZEN_WIDTH,
                        // z-4: above the frozen header row (z-3) it slides under.
                        position: 'sticky', right: (FROZEN_COLS.length - 1 - i) * FROZEN_WIDTH,
                        zIndex: 4, background: '#002159',
                        boxShadow: i === 0 ? '-2px 0 4px rgba(0,0,0,0.15)' : 'none',
                      }}>
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: HEADER_COLORS[activeCols[0]?.group] || '#ccc' }}>
                          rules.labor_task
                        </span>
                        {fc.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.row_id}>
                      <td><strong>{r.row_id}</strong></td>
                      {activeCols.map((col) => (
                        <td key={col.key} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatValue(r[col.key], col.key)}
                        </td>
                      ))}
                      {FROZEN_COLS.map((fc, i) => {
                        const val = r[fc.key] || '';
                        const nextVal = val === '' ? 'Y' : val === 'Y' ? 'N' : '';
                        return (
                          <td key={fc.key} style={{
                            textAlign: 'center', padding: '4px 6px',
                            width: FROZEN_WIDTH, minWidth: FROZEN_WIDTH, maxWidth: FROZEN_WIDTH,
                            position: 'sticky', right: (FROZEN_COLS.length - 1 - i) * FROZEN_WIDTH,
                            zIndex: 1, background: '#fff',
                            boxShadow: i === 0 ? '-2px 0 4px rgba(0,0,0,0.06)' : 'none',
                          }}>
                            {canEdit ? (
                              <button
                                onClick={() => handleExclChange(r, fc.key, nextVal)}
                                disabled={saving === `${r.row_id}_${fc.key}`}
                                style={{
                                  width: 50, padding: '3px 0', fontSize: 11, fontWeight: 600,
                                  borderRadius: 12, border: 'none', cursor: 'pointer',
                                  background: val === 'Y' ? '#f8d7da' : val === 'N' ? '#d4edda' : '#e9ecef',
                                  color: val === 'Y' ? '#721c24' : val === 'N' ? '#155724' : '#999',
                                }}
                              >
                                {val === 'Y' ? 'Y' : val === 'N' ? 'N' : '—'}
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: '#666' }}>
                                {val === 'Y' ? 'Y' : val === 'N' ? 'N' : '—'}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
                  Loading more...
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
                  All {total} rows loaded
                </div>
              )}
            </div>
          )
      }
    </>
  );
}
